import { useEffect, useState } from 'react';

/** A "Later" mutes the nudge for an hour, as in stream-chat's widget. */
const DISMISS_MS = 60 * 60 * 1000;

/** Long enough that the nudge doesn't race the host page's own first paint. */
const SETTLE_MS = 2_000;

const KEY = 'stream-chat-emp:callout-dismissed';

export interface UseCallout {
  visible: boolean;
  dismiss: () => void;
}

/**
 * Whether to nudge the employer about chats waiting on them, and the "Later"
 * that shuts it up.
 *
 * The dismissal outlives the page on purpose: this thing renders on every host
 * page, so a per-mount dismissal would mean "Later" lasts until the employer
 * clicks anything, which reads as ignoring them.
 *
 * Expiry is evaluated at render rather than on a timer — `usePendingReplies`
 * re-polls (and so re-renders this) every minute, far tighter than the hour
 * being timed.
 */
export function useCallout(count: number, panelOpen: boolean): UseCallout {
  const [settled, setSettled] = useState(false);
  const [dismissedUntil, setDismissedUntil] = useState(readDismiss);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(timer);
  }, []);

  return {
    visible: settled && count > 0 && !panelOpen && Date.now() >= dismissedUntil,
    dismiss: () => {
      const until = Date.now() + DISMISS_MS;
      try {
        localStorage.setItem(KEY, String(until));
      } catch {
        // Private mode or a full quota: the dismissal is then only as durable as
        // the page, which beats failing to dismiss at all.
      }
      setDismissedUntil(until);
    },
  };
}

function readDismiss(): number {
  try {
    return Number(localStorage.getItem(KEY)) || 0;
  } catch {
    return 0;
  }
}
